import { useState } from 'react';
import { BookOpen, Pencil, Sparkles, TriangleAlert } from 'lucide-react';
import { Badge, Button } from '../../../school_panel/components/ui';
import type { AuthoringQuestionType, ImportedQuestion } from '../../../school_panel/api/types';
import { TYPE_ICON, TYPE_LABEL } from './QuestionTypePicker';
import { gradeLabel } from '../../../school_panel/lib/gradeLabel';
import AnswerPreview, { withBlanks } from './AnswerPreview';

/** The AI's questions, to check and add. Questions with problems stay unticked until fixed in the editor. */
export default function ImportReview({ items, adding, failures, onAdd, onEdit, onDiscard, onAnnotate }: {
  items: ImportedQuestion[];
  adding: boolean;
  failures: Record<number, string>;
  onAdd: (indexes: number[]) => void;
  onEdit: (index: number) => void;
  onDiscard: () => void;
  /** Opens the chapters / explanations / references dialog again. */
  onAnnotate?: () => void;
}) {
  const [picked, setPicked] = useState<Set<number>>(() => new Set(items.flatMap((it, i) => (it.problems.length ? [] : [i]))));
  const toggle = (i: number) => setPicked((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const count = Array.from(picked).filter((i) => i < items.length).length;
  return (
    <section aria-label="Imported questions" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">
          The AI found <strong className="text-ink">{items.length}</strong> {items.length === 1 ? 'question' : 'questions'}. Tick the ones to add; answers marked “AI” were worked out by the AI, so check them.
        </p>
        <div className="flex gap-1">
          {onAnnotate && (
            <Button variant="secondary" size="sm" icon={<Sparkles size={14} aria-hidden />} onClick={onAnnotate} disabled={adding}>
              Chapters &amp; explanations
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDiscard} disabled={adding}>Discard</Button>
        </div>
      </div>
      <ol className="space-y-2">
        {items.map((it, i) => {
          const t = it.question.type as AuthoringQuestionType;
          const blocked = it.problems.length > 0;
          return (
            <li key={i} className={`rounded-[14px] border p-3 ${picked.has(i) ? 'border-navy bg-note' : 'border-line bg-surface'}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-navy" aria-label={`Add imported question ${i + 1}`}
                  checked={picked.has(i)} disabled={blocked || adding} onChange={() => toggle(i)} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    <Badge tone="info"><span className="mr-1 inline-flex">{TYPE_ICON[t]}</span>{TYPE_LABEL[t]}</Badge>
                    {it.answer_source === 'document'
                      ? <Badge tone="success">Answer from the file</Badge>
                      : <Badge tone="warning">AI answer · check it</Badge>}
                  </div>
                  <p className="mt-1.5 break-words text-sm text-ink">{withBlanks(it.question.text)}</p>
                  <AnswerPreview q={it.question} />
                  {(it.chapter || it.question.reference || it.question.explanation) && (
                    <div className="mt-2 space-y-1 border-t border-hairline pt-2 text-xs">
                      {it.chapter && (
                        <p className="text-ink"><span className="font-semibold">Chapter:</span> {it.chapter.name}
                          {it.chapter.grade && <span className="text-muted"> · {gradeLabel(it.chapter.grade)}</span>}</p>
                      )}
                      {it.question.explanation && <p className="line-clamp-3 text-muted"><span className="font-semibold text-ink">Explanation:</span> {it.question.explanation}</p>}
                      {it.question.reference && (
                        <p className="flex items-start gap-1 text-muted"><BookOpen size={12} aria-hidden className="mt-0.5 flex-none" />
                          <span className="line-clamp-2">{it.question.reference}</span></p>
                      )}
                    </div>
                  )}
                  {it.note && <p className="mt-1 text-xs text-muted">Note: {it.note}</p>}
                  {blocked && (
                    <p className="mt-1.5 flex items-start gap-1.5 text-xs text-danger-ink">
                      <TriangleAlert size={14} aria-hidden className="mt-px flex-none" />{it.problems.join(' ')} Edit it to add it.
                    </p>
                  )}
                  {failures[i] && <p role="alert" className="mt-1.5 text-xs text-danger-ink">Not added: {failures[i]}</p>}
                </div>
                <Button variant="ghost" size="sm" icon={<Pencil size={14} aria-hidden />} onClick={() => onEdit(i)} disabled={adding}
                  aria-label={`Edit imported question ${i + 1}`}>Edit</Button>
              </div>
            </li>
          );
        })}
      </ol>
      <div className="sticky bottom-0 flex justify-end border-t border-hairline bg-surface pt-3">
        <Button onClick={() => onAdd(Array.from(picked).filter((i) => i < items.length).sort((a, b) => a - b))} disabled={count === 0} loading={adding}>
          Add {count} {count === 1 ? 'question' : 'questions'}
        </Button>
      </div>
    </section>
  );
}
